import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useCommon } from "../context/CommonContext";
import { useLanguage } from "../context/LanguageProvider";

/**
 * IATTestThankYou
 * ----------------
 * This component renders a "Thank You" (or transition) screen shown after
 * participants complete the Implicit Association Test (IAT) portion.
 * 
 * It:
 *  - Displays a thank-you message or bridge text (fetched from a context variable).
 *  - Waits for the participant to press the Space bar to continue.
 *  - Navigates the user to the "Explicit Questions" phase of the study.
 */
export const IATTestThankYou = () => {

    // Hook from React Router to programmatically navigate between routes
    const navigate = useNavigate();

    // Pull common data from global context
    // implicitExplicitText is likely a mutable ref object (`.current`) with dynamic text.
    const { implicitExplicitText } = useCommon();

    // Pull the translation function for i18n support
    // translation("key") returns a localized string according to the current language setting
    const { translation } = useLanguage();

    /**
     * Event handler: runs on any key press
     * @param {KeyboardEvent} event - the keyboard event from the browser
     */
    const handleKeyPress = (event) => {
        // We check specifically for the space bar key.
        // NOTE: event.key === ' ' works because on most keyboards the space bar returns a single space string.
        if (event.key === " ") {
            // Programmatically navigate to the next screen: explicit questions section
            navigate("/ExplicitQuestions");
        }
    };

    /**
     * useEffect is used here to register & clean up the keyboard listener
     * The empty dependency array `[]` causes this effect to run only once on mount
     */
    useEffect(() => {
        // Add global keydown listener so the component responds to Space bar presses
        window.addEventListener("keydown", handleKeyPress);

        // Cleanup: remove the keydown listener when component unmounts
        return () => {
            window.removeEventListener("keydown", handleKeyPress);
        };
    }, []); // run only once at mount

    return (
        <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
            {/* Optional title heading commented out — might be toggled on if a visible "Thank You!" is preferred
            <h3 className="text-3xl text-gray-700 mb-6 font-bold text-left w-full max-w-3xl ml-4">
                Thank You!
            </h3>
            */}

            {/* Main message: pulled from common context's `implicitExplicitText.current` */}
            <h4 className="text-3xl text-gray-800 mb-6">
                {implicitExplicitText.current}
            </h4>

            {/* Instruction to participant: Press Space to continue, localized via translation */}
            <h3 className="text-3xl text-gray-500 mb-6">
                {translation("IATThankYou_PressSpaceToContinue")}
            </h3>
        </div>
    );
};
